import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import { useAppSelector } from '@/store/hooks'

interface MemberSelectProps {
  selectedMemberId: string | null
  selectedMemberName: string | null
  onSelect: (memberId: string, memberName: string, memberEmail: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  workspaceId: string
  label: string
  // 새로운 props: 외부에서 멤버 데이터를 직접 전달받을 수 있음
  members?: OrgMemberResponse[]
  isLoading?: boolean
  membersError?: string | null
  // Control whether to show "전체" option (for filtering vs form input)
  showAllOption?: boolean
}

// Custom hook for workspace member data loading
const useWorkspaceMembers = (workspaceId: string) => {
  const [members, setMembers] = useState<OrgMemberResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !selectedWorkspace?.org_id) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const response: PaginatedResponse = await OrgMemberService.appsOrganizationViewsListOrgMembers({
        orgId: selectedWorkspace.org_id,
        workspaceId,
        page: 1,
        perPage: 1000 // Load all org members
      })
      
      // Transform OrgMemberResponse to WorkspaceMemberResponse format
      const orgMembers = response.data as OrgMemberResponse[]
      const workspaceMembers: OrgMemberResponse[] = orgMembers
        .filter(member => member.workspaces?.some(ws => ws.id === workspaceId))
        .map(member => ({
          id: member.id,
          account_id: member.account_id,
          account_name: member.account_name,
          account_email: member.account_email,
          account_phone: '', // Not available in OrgMemberResponse
          workspace_id: workspaceId,
          workspace_name: member.workspaces?.find(ws => ws.id === workspaceId)?.name || ''
        }))
      
      setMembers(workspaceMembers)
    } catch (err) {
      console.error('Failed to fetch workspace members:', err)
      setError('멤버 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, selectedWorkspace?.org_id])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  return { members, isLoading, error, refetch: fetchMembers }
}

const MemberSelect: React.FC<MemberSelectProps> = ({
  selectedMemberId,
  selectedMemberName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = "멤버를 선택하세요",
  workspaceId,
  // 외부에서 전달된 멤버 데이터
  members: externalMembers,
  isLoading: externalIsLoading,
  membersError: externalMembersError,
  // Control "전체" option visibility (default: false for form inputs)
  showAllOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // 외부 데이터가 있으면 사용하고, 없으면 내부 hook 사용 (하위 호환성)
  const internalData = useWorkspaceMembers(externalMembers ? '' : workspaceId) // 외부 데이터가 있으면 내부 API 호출 안함
  
  const members = externalMembers || internalData.members
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalData.isLoading
  const fetchError = externalMembersError || internalData.error

  // Filter members based on search term (search member name only)
  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return members
    const searchLower = searchTerm.toLowerCase().trim()
    return members.filter(member =>
      member.account_name.toLowerCase().includes(searchLower)
    )
  }, [members, searchTerm])

  const handleSelect = (member: OrgMemberResponse) => {
    // Extract name from account_email (assuming email format like "name@domain.com")
    onSelect(member.id, member.account_name, member.account_email)
    setIsOpen(false)
    setSearchTerm('') // Clear search when selecting
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '', '')
    setSearchTerm('') // Clear search when clearing selection
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('') // Clear search when opening dropdown
      }
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedMemberName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span>{selectedMemberName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedMemberId && selectedMemberId.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown 
              className="w-5 h-5" 
              style={{ color: designTokens.colors.gray[3] }} 
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="담당자명 검색..."
                className="w-full pl-2 pr-3 py-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6]
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
            <div 
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              멤버 목록을 불러오는 중...
            </div>
          ) : fetchError ? (
            <div 
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.error.DEFAULT
              }}
            >
              {fetchError}
            </div>
          ) : members.length === 0 ? (
            <div
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              워크스페이스 멤버가 없습니다.
            </div>
          ) : filteredMembers.length === 0 ? (
            <div
              className="p-3 text-center"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[4]
              }}
            >
              검색 결과가 없습니다.
            </div>
          ) : (
            <>
              {/* All Members Option - only show if showAllOption is true and no search term */}
              {showAllOption && !searchTerm.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect('', '', '')
                    setIsOpen(false)
                    setSearchTerm('')
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                >
                  전체
                </button>
              )}

              {filteredMembers.map((member) => {
                const displayName = member.account_name
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleSelect(member)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0]
                    }}
                  >
                    <div>
                      <div 
                        className="font-medium"
                        style={{
                          fontSize: '16px',
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        {displayName}
                      </div>
                      <div 
                        className="text-sm"
                        style={{ 
                          fontSize: '14px',
                          color: designTokens.colors.gray[4] 
                        }}
                      >
                        {member.account_email}
                      </div>
                    </div>
                  </button>
                )
              })}
            </>
          )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div 
          className="mt-1 text-sm"
          style={{ 
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default MemberSelect